import { defineStore } from "pinia";
import type { MicrosoftCloudAgreementGetResult } from "~/types";

export const useMicrosoftCloudAgreementStore = defineStore(
  "microsoftCloudAgreementStore",
  () => {
    const { restGet, restPost } = useWebhusetApi();
    const authStore = useAuthStore();

    const loading = ref<boolean>();
    const agreementRequired = ref<boolean>();

    const agreementResult = ref<MicrosoftCloudAgreementGetResult>();

    const hasSignedAgreement = computed(() => {
      if (agreementResult.value === undefined) {
        return undefined;
      }

      return agreementResult.value.has_accepted_agreement === true;
    });

    const signatureDetails = computed(() => {
      if (agreementResult.value === undefined) {
        return undefined;
      }

      return {
        name: `${agreementResult.value.signer_firstname} ${agreementResult.value.signer_lastname}`,
        email: agreementResult.value.signer_email,
        date: agreementResult.value.acceptance_datetime
          ? new Date(agreementResult.value.acceptance_datetime).toLocaleString()
          : undefined,
      };
    });

    const acceptAgreement = async (
      firstName: string,
      lastName: string,
      email: string
    ) => {
      try {
        loading.value = true;

        await restPost("/office365/microsoft-cloud-agreement", {
          signer_firstname: firstName,
          signer_lastname: lastName,
          signer_email: email,
        });

        agreementResult.value = {
          has_accepted_agreement: true,
          signer_firstname: firstName,
          signer_lastname: lastName,
          signer_email: email,
          acceptance_datetime: new Date().toISOString(),
          status: 1,
          errcode: 0,
        };
      } catch (err) {
        console.error("Error signing Cloud Agreement", err);
        throw err;
      } finally {
        loading.value = false;
      }
    };

    watch(
      () => authStore.customerId,
      () => {
        agreementResult.value = undefined;
      }
    );

    watch(
      [() => agreementRequired.value, () => authStore.customerId],
      async ([isRequired]) => {
        if (
          isRequired &&
          agreementResult.value === undefined &&
          !loading.value &&
          authStore.customerId
        ) {
          try {
            loading.value = true;

            // Check if user has signed the agreement
            agreementResult.value =
              await restGet<MicrosoftCloudAgreementGetResult>(
                "/office365/microsoft-cloud-agreement"
              );
          } catch (err) {
            console.error("Error getting Cloud Agreement status", err);
          } finally {
            loading.value = false;
          }
        }
      },
      {
        immediate: true,
      }
    );

    return {
      agreementRequired,
      loading,
      hasSignedAgreement,
      signatureDetails,
      acceptAgreement,
      setAgreementRequired(value: boolean) {
        agreementRequired.value = value;
      },
    };
  }
);
